import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import Select from "react-select";
import {
  CheckPermission,
  LoadingSpinner,
  customOption,
  applyClassToCharacters,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles, compressImageFile } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";
import AddNewBankAccount from "../transfers/amanatToCashInHand/AddNewBankAccount";
import { addMember } from "../../member/ApiMember";
import { FaPlus } from "react-icons/fa";

const CustomInput = ({ autoFocus, label, name, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      {<GetLanguageString props={label} />}
    </Label>
    <Input
      autoFocus={autoFocus}
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

// ---------------- Main Component (clone without dimensions and serial number) ----------------
const OtherIncomeLoanFromMarkaz = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  // ---------- State ----------
  const [loading, setLoading] = useState(false);

  const [unitBankAccounts, setUnitBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [refreshBankList, setRefreshBankList] = useState(false);
  const [handleAddNew, setHandleAddNew] = useState(false);

  const [formData, setFormData] = useState({
    selectedFinanceCategory: null,
    selectedAccount: null,
    rafiqBankId: null,
    date: new Date(),
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});

  const [attachedFile, setAttachedFile] = useState(null);

  // ---------- Handlers ----------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleBankAccountChange = (option) => {
    setSelectedBankAccount(option);
    setFormData((prev) => ({ ...prev, selectedBankAccount: option }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 1500 * 1024; // 1.5 MB
    let processedFile = file;

    if (file.type && file.type.startsWith("image/") && file.size > MAX_SIZE) {
      try {
        processedFile = await compressImageFile(file, 1400 * 1024);
      } catch (err) {
        console.error("Image compression error:", err);
      }
    }

    if (processedFile.size > MAX_SIZE) {
      setAttachedFile(null);
      setErrors((prev) => ({
        ...prev,
        attachedFile: <GetLanguageString props="halqa_loan_letter_size_limit" />,
      }));
      e.target.value = "";
    } else {
      setAttachedFile(processedFile);
      setErrors((prev) => ({ ...prev, attachedFile: "" }));
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const isValid = validate();
    if (!isValid) {
      return;
    }
    const payload = new FormData();
    payload.append("rafiqBankId", formData.selectedBankAccount?.value ?? 0);
    payload.append("date", formData.date ? formData.date.toISOString() : new Date().toISOString());
    payload.append("amount", formData.amount);
    payload.append("memo", formData.memo);
    payload.append("unitId", loggedInUnitId);
    if (formData.selectedFinanceCategory) {
      if (formData.selectedFinanceCategory.label1) {
        payload.append("category.label1", formData.selectedFinanceCategory.label1);
      }
      if (formData.selectedFinanceCategory.value) {
        payload.append("category.value", formData.selectedFinanceCategory.value);
      }
    }
    if (attachedFile) {
      payload.append("File", attachedFile);
    }

    setLoading(true);
    ApiFinanceLocal()
      .AddOtherIncomeLoanFromMarkaz(payload)
      .then((res) => {
        const data = res.data;

        if (data?.statusCode === "200") {
          swal({
            title: "Success",
            text:
              t(data.message) ||
              t("finance_other_income_loan_from_markaz_requested"),
            icon: "success",
            buttons: "OK",
          }).then(() => {
            history.goBack();
          });
          return;
        }

        swal({
          title: "Error",
          text: data?.message || "Failed to add Other Income",
          icon: "error",
          buttons: "OK",
        });

        setLoading(false);
      })
      .catch(() => {
        swal({
          title: "Error",
          text: "Unable to connect to server. Please try again.",
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
  };

  // ---------- Effects ----------
  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoriesListByType("income")
      .then((res) => {
        // Prefer a category that references markaz; fallback to first
        const preferred = res.data.find(
          (c) => c.label1 && c.label1.includes("loan_markaz"),
        );
        setFormData((prev) => ({
          ...prev,
          selectedFinanceCategory: preferred || res.data[0] || null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: err,
          text: err.response?.data,
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading(false));
  }, [unitTypeName]);

  // Fetch unit bank accounts
  useEffect(() => {
    if (!loggedInUnitId) return;
    addMember()
      .GetUnitBankAccounts(loggedInUnitId)
      .then((res) => {
        const list = res.data || [];
        setUnitBankAccounts(list);
        if (list.length > 0 && !refreshBankList) {
          setSelectedBankAccount(list[0]);
          setFormData((prev) => ({ ...prev, selectedBankAccount: list[0] }));
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setRefreshBankList(false));
  }, [loggedInUnitId, refreshBankList]);

  // ---------- Validation ----------
  const validate = () => {
    const temp = {};
    temp.amount = formData.amount ? (
      ""
    ) : (
      <GetLanguageString props="funds_add_fund_amount_paid_error" />
    );

    temp.memo = formData.memo && formData.memo.trim() !== "" ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );

    const maxSize = 2 * 1024 * 1024; // 2MB limit for Finance Software attachments
    if (!attachedFile) {
      temp.attachedFile = (
        <GetLanguageString props="halqa_loan_letter_required" />
      );
    } else if (attachedFile.size > maxSize) {
      temp.attachedFile = (
        <GetLanguageString props="halqa_loan_letter_size_limit" />
      );
    } else {
      temp.attachedFile = "";
    }

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  // ---------- Render ----------
  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {<GetLanguageString props="funds_other_income_loan_from_markaz" />}
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row>
                {handleAddNew && (
                  <AddNewBankAccount
                    setModal={setHandleAddNew}
                    getAddedBankAccount={setSelectedBankAccount}
                    setRefreshBankList={setRefreshBankList}
                  />
                )}

                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="fund_bank_account" />}
                    </Label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <div style={{ flex: 1 }}>
                        <Select
                          components={{ Option: customOption }}
                          options={unitBankAccounts}
                          value={
                            selectedBankAccount
                              ? {
                                  value: selectedBankAccount.value,
                                  label: applyClassToCharacters(
                                    selectedBankAccount.label,
                                    "ur",
                                  ),
                                }
                              : null
                          }
                          onChange={handleBankAccountChange}
                          placeholder={t("member_select")}
                          classNamePrefix="select"
                        />
                      </div>

                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setHandleAddNew(true)}
                        style={{
                          height: "38px", // matches Select height
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 12px", // small compact button
                        }}
                      >
                        <FaPlus
                          color="#f7f7f7ff"
                          size={11}
                          style={{ marginRight: 5, marginBottom: 1 }}
                        />
                        {t("tutorial_add_category")}
                      </Button>
                    </div>
                  </FormGroup>
                </Col>

                {/* File Upload Field for Halqa Loan Letter */}
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="halqa_loan_letter_attachment" />}
                    </Label>
                    <Input
                      type="file"
                      accept="image/*,application/pdf"
                      name="file"
                      className="form-control"
                      onChange={handleFileChange}
                      invalid={!!errors.attachedFile}
                    />
                    {errors.attachedFile && (
                      <h4 className="text-error mt-1">{errors.attachedFile}</h4>
                    )}
                    {attachedFile && (
                      <div
                        className="d-flex align-items-center justify-content-between p-2 mt-2 rounded"
                        style={{
                          backgroundColor: "#ECFDF5",
                          border: "1px solid #A7F3D0",
                          fontSize: "12px",
                          color: "#065F46",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="fa fa-file text-success"></i>
                          <span className="text-truncate fw-bold">{attachedFile.name}</span>
                          <span className="badge bg-light text-muted border px-1">
                            {(attachedFile.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="text-danger p-0 ms-2 text-decoration-none"
                          onClick={() => setAttachedFile(null)}
                          title={t("remove_file") || "Remove file"}
                          type="button"
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {/* Amount, Date, and Memo aligned in a 3-column row */}
                <Col md="4">
                  <CustomInput
                    autoFocus
                    name="amount"
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>

                <Col md="4">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      minDate={getMinMaxDates(formData.date).minDate}
                      maxDate={getMinMaxDates(formData.date).maxDate}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.date && (
                      <h4 className="text-error">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="4">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
              </Row>

              <Row>
                <Col md="9"></Col>
                <Col md="3" className="text-end">
                  {CheckPermission("CanAddFunds") ? (
                    <>
                      <Button color="white" onClick={() => history.goBack()}>
                        {<GetLanguageString props="common_cancel" />}
                      </Button>
                      &nbsp;&nbsp;
                      <Button
                        disabled={loading}
                        color="primary"
                        onClick={() => handleSubmit(false)}
                      >
                        {<GetLanguageString props="request_loan" />}
                      </Button>
                    </>
                  ) : null}
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(OtherIncomeLoanFromMarkaz);
